import catalog from '../../data/product-ideas.json';
export const productCatalog = catalog;
export interface ProductChoice {
  group: string;
  product: string;
  format: string;
  packaging: string;
}
interface CustomerBriefMetadata {
  origin: 'customer-brief-v2' | 'customer-brief-v3' | 'customer-brief-v4';
  notes: string;
  choice: ProductChoice;
  services: string[];
  references: string[];
}
const legacyBlock = /\[แนวคิดสินค้า — ยังไม่ยืนยัน\][\s\S]*?\[\/แนวคิดสินค้า\]/;
const metadataStart = '[ข้อมูลบรีฟลูกค้า v2]';
const metadataEnd = '[/ข้อมูลบรีฟลูกค้า v2]';
const metadataBlock = /\[ข้อมูลบรีฟลูกค้า v2\]\n([^\n]*)\n\[\/ข้อมูลบรีฟลูกค้า v2\]$/;
function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function strings(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item: unknown) => typeof item === 'string');
}
interface ParsedMetadata extends CustomerBriefMetadata {
  startIndex: number;
  literalNotes: boolean;
}
function structuralMetadata(summary: unknown): ParsedMetadata | null {
  if (typeof summary !== 'string') return null;
  const match = metadataBlock.exec(summary);
  const encoded = match?.[1];
  if (!encoded) return null;
  try {
    const value: unknown = JSON.parse(encoded);
    if (
      !object(value) ||
      (value.origin !== 'customer-brief-v2' &&
        value.origin !== 'customer-brief-v3' &&
        value.origin !== 'customer-brief-v4') ||
      typeof value.noteLength !== 'number' ||
      !Number.isSafeInteger(value.noteLength) ||
      value.noteLength < 0 ||
      value.noteLength > (match?.index ?? 0) ||
      !object(value.choice) ||
      !strings(value.services) ||
      !strings(value.references) ||
      !value.references.every((url) => Boolean(url.trim()) && publicReference(url))
    )
      return null;
    const choice = value.choice;
    if (
      typeof choice.group !== 'string' ||
      typeof choice.product !== 'string' ||
      typeof choice.format !== 'string' ||
      typeof choice.packaging !== 'string'
    )
      return null;
    return {
      origin: value.origin,
      startIndex: match?.index ?? 0,
      literalNotes: value.literalNotes === true,
      notes:
        value.origin === 'customer-brief-v2'
          ? decodeNotes(summary.slice(0, value.noteLength))
          : summary.slice(0, value.noteLength),
      choice: {
        group: choice.group,
        product: choice.product,
        format: choice.format,
        packaging: choice.packaging,
      },
      services: value.services,
      references: value.references,
    };
  } catch {
    return null;
  }
}
function hasBriefFields(value: CustomerBriefMetadata): boolean {
  return Boolean(value.choice.group || value.services.length || value.references.length);
}
function recognizedLiteralNotes(notes: string): boolean {
  return structuralMetadata(notes) !== null || Boolean(legacyChoice(notes).group);
}
function metadata(summary: unknown): CustomerBriefMetadata | null {
  const parsed = structuralMetadata(summary);
  if (!parsed || typeof summary !== 'string') return null;
  if (parsed.origin === 'customer-brief-v2') return parsed;
  const body = [parsed.notes, readableMetadataBody(parsed)].filter(Boolean).join('\n\n');
  if (summary.slice(0, parsed.startIndex) !== (body ? body + '\n\n' : '')) return null;
  if (parsed.origin === 'customer-brief-v4') {
    if (!parsed.literalNotes || hasBriefFields(parsed)) return null;
  } else if (!hasBriefFields(parsed) && !recognizedLiteralNotes(parsed.notes)) return null;
  return parsed;
}
function services(values: readonly string[]): string[] {
  return values.filter((value) => value.trim() !== '');
}
export function readCustomerServices(summary: unknown): string[] {
  return metadata(summary)?.services ?? [];
}
export function readCustomerReferences(summary: unknown): string[] {
  return metadata(summary)?.references ?? [];
}
export function productChoiceNotes(summary: unknown): string {
  const decoded = metadata(summary);
  if (decoded) return decoded.notes;
  const text = typeof summary === 'string' ? summary : '',
    match = readProductChoice(text).group ? legacyBlock.exec(text) : null,
    notes = match ? text.replace(legacyBlock, '') : text;
  return match && match.index + match[0].length === text.length && notes.endsWith('\n\n')
    ? notes.slice(0, -2)
    : notes;
}
export function readProductChoice(summary: unknown): ProductChoice {
  const decoded = metadata(summary);
  if (decoded) return decoded.choice;
  return legacyChoice(summary);
}
function legacyChoice(summary: unknown): ProductChoice {
  const text = typeof summary === 'string' ? (legacyBlock.exec(summary)?.[0] ?? '') : '';
  const value = (label: string) =>
    text
      .split('\n')
      .find((line) => line.startsWith(label + ': '))
      ?.slice(label.length + 2) ?? '';
  const group = catalog.categories.find((g) => g.label === value('หมวด'));
  const find = (items: { id: string; label: string }[], label: string) =>
    items.find((item) => item.label === value(label))?.id ?? '';
  return {
    group: group?.id ?? '',
    product: find(group?.products ?? [], 'สินค้า'),
    format: find(group?.formats ?? [], 'รูปแบบ'),
    packaging: find(group?.packaging ?? [], 'บรรจุภัณฑ์'),
  };
}
function choiceRecap(choice: ProductChoice): string {
  const group = catalog.categories.find((g) => g.id === choice.group);
  if (!group) return '';
  const label = (items: { id: string; label: string }[], id: string) =>
    items.find((item) => item.id === id)?.label ?? 'ยังไม่เลือก';
  return `หมวด: ${group.label} · สินค้า: ${label(group.products, choice.product)} · รูปแบบ: ${label(group.formats, choice.format)} · บรรจุภัณฑ์: ${label(group.packaging, choice.packaging)}`;
}
export function productChoiceRecap(summary: unknown): string {
  return choiceRecap(readProductChoice(summary));
}
function currentMetadata(summary: unknown): CustomerBriefMetadata {
  return (
    metadata(summary) ?? {
      origin: 'customer-brief-v2',
      notes: productChoiceNotes(summary),
      choice: readProductChoice(summary),
      services: [],
      references: [],
    }
  );
}
function decodeNotes(notes: string): string {
  return notes.replace(/\\(\\|u005b|u005d)/g, (_match: string, token: string) =>
    token === 'u005b' ? '[' : token === 'u005d' ? ']' : '\\',
  );
}
function readableMetadataBody(value: CustomerBriefMetadata): string {
  const safeBody = (text: string) =>
    text
      .replaceAll(metadataStart, '［ข้อมูลบรีฟลูกค้า v2］')
      .replaceAll(metadataEnd, '［/ข้อมูลบรีฟลูกค้า v2］')
      .replaceAll('[แนวคิดสินค้า — ยังไม่ยืนยัน]', '［แนวคิดสินค้า — ยังไม่ยืนยัน］')
      .replaceAll('[/แนวคิดสินค้า]', '［/แนวคิดสินค้า］');
  return [
    choiceRecap(value.choice),
    value.services.length ? 'บริการที่สนใจ: ' + value.services.join(' · ') : '',
    ...value.references,
  ]
    .filter(Boolean)
    .map(safeBody)
    .join('\n\n');
}
function summary(value: CustomerBriefMetadata): string {
  const literalFrame = !hasBriefFields(value);
  if (literalFrame && !recognizedLiteralNotes(value.notes)) return value.notes;
  const notes = value.notes;
  const body = readableMetadataBody(value);
  const payload = JSON.stringify({
    origin: literalFrame ? 'customer-brief-v4' : 'customer-brief-v3',
    ...(literalFrame ? { literalNotes: true } : {}),
    noteLength: notes.length,
    choice: value.choice,
    services: value.services,
    references: value.references,
  })
    .replaceAll(metadataStart, '\\u005bข้อมูลบรีฟลูกค้า v2\\u005d')
    .replaceAll(metadataEnd, '\\u005b/ข้อมูลบรีฟลูกค้า v2\\u005d')
    .replaceAll('[แนวคิดสินค้า — ยังไม่ยืนยัน]', '\\u005bแนวคิดสินค้า — ยังไม่ยืนยัน\\u005d')
    .replaceAll('[/แนวคิดสินค้า]', '\\u005b/แนวคิดสินค้า\\u005d');
  return [notes, body, `${metadataStart}\n${payload}\n${metadataEnd}`].filter(Boolean).join('\n\n');
}
export function customerBriefBudget(values: Record<string, unknown>) {
  const length = typeof values.summary === 'string' ? values.summary.length : 0;
  return { length, remaining: 4000 - length, valid: length <= 4000 };
}
export function updateProductNotes(previous: unknown, notes: string): string {
  return summary({ ...currentMetadata(previous), notes });
}
export function applyProductChoice(values: Record<string, unknown>, choice: ProductChoice) {
  const group = catalog.categories.find((g) => g.id === choice.group);
  if (!group) return values;
  return {
    ...values,
    category:
      group.id === 'cosmetics' ? 'cosmetic' : group.id === 'supplements' ? 'supplement' : 'other',
    summary: summary({ ...currentMetadata(values.summary), choice }),
  };
}
// These writers are invoked only by the customer controls. Hidden backend fields are never a metadata source.
export function updateCustomerServices(
  values: Record<string, unknown>,
  input: string[],
): Record<string, unknown> {
  return {
    ...values,
    requestedServices: input,
    summary: summary({ ...currentMetadata(values.summary), services: services(input) }),
  };
}
export function addCustomerReference(values: Record<string, unknown>, url: string) {
  if (!url.trim() || !publicReference(url)) return values;
  const previous = currentMetadata(values.summary),
    references = [...new Set([...previous.references, url.trim()])];
  const source = typeof values.source === 'string' ? values.source : '';
  return {
    ...values,
    source: [source, url.trim()].filter(Boolean).join('\n'),
    summary: summary({ ...previous, references }),
  };
}
export function removeCustomerReference(values: Record<string, unknown>, url: string) {
  const previous = currentMetadata(values.summary);
  const source =
    typeof values.source === 'string'
      ? values.source
          .split('\n')
          .filter((line) => line !== url)
          .join('\n')
      : '';
  return {
    ...values,
    source,
    summary: summary({
      ...previous,
      references: previous.references.filter((value) => value !== url),
    }),
  };
}
export function customerBriefForSave(values: Record<string, unknown>): Record<string, unknown> {
  return {
    ...values,
    requestedServices: strings(values.requestedServices) ? services(values.requestedServices) : [],
  };
}
export function hydrateCustomerBrief(content: Record<string, unknown>): Record<string, unknown> {
  return { ...content, requestedServices: readCustomerServices(content.summary) };
}
export function customerBriefEquivalent(
  values: Record<string, unknown>,
  saved: Record<string, unknown>,
): boolean {
  const normalize = (value: Record<string, unknown>, hydrate: boolean) => {
    const current: Record<string, unknown> = hydrate
      ? hydrateCustomerBrief(value)
      : {
          ...value,
          requestedServices: strings(value.requestedServices)
            ? services(value.requestedServices)
            : [],
        };
    return JSON.stringify(
      Object.fromEntries(
        Object.keys(current)
          .sort()
          .map((key) => [key, current[key]]),
      ),
    );
  };
  return normalize(values, false) === normalize(saved, true);
}
export function publicReference(value: string): boolean {
  if (!value.trim()) return true;
  try {
    const url = new URL(value);
    return (
      ['https:', 'http:'].includes(url.protocol) &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}
